import { Fragment, useCallback, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';

import { Button } from '#/components/Button/Button';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiTableOfContentsParent } from '#/modules/cms/api/types/CmsApiTableOfContents';

import {
  MobileTableOfContentsChildRow,
  MobileTableOfContentsChildren,
  MobileTableOfContentsContainer,
  MobileTableOfContentsContent,
  MobileTableOfContentsHandle,
  MobileTableOfContentsParentRow,
  MobileTableOfContentsParentRowIcon,
} from './CmsMobileTableOfContents.styles';

type Props = {
  parents: CmsApiTableOfContentsParent[];
  hasButtonBar: boolean;
};

export function CmsMobileTableOfContents({ parents, hasButtonBar }: Props) {
  const { i18n } = useI18n();

  const [open, setOpen] = useState(false);
  const [bottom, setBottom] = useState(-300);
  const [selectedParent, setSelectedParent] = useState<string | null>(null);

  let bottomOffset = 0;
  if (hasButtonBar) {
    const el = document.getElementById('button-bar-container');
    if (el) {
      bottomOffset = el.offsetHeight;
    }
  }

  useEffect(() => {
    if (open) {
      setBottom(bottomOffset);
    } else {
      const el = document.getElementById('MobileTableOfContentsContent');
      if (!el) {
        return;
      }
      setBottom(bottomOffset - el.offsetHeight);
    }
  }, [bottomOffset, open]);

  const navigateToChapter = useCallback((slug: string) => {
    setOpen(false);
    const el = document.getElementById(slug);
    el?.scrollIntoView();
  }, []);

  const clickParent = (parent: CmsApiTableOfContentsParent) => {
    if (parent.url !== selectedParent && parent.children.length > 0) {
      setSelectedParent(parent.url);
    } else {
      navigateToChapter(parent.url);
    }
  };

  const clickChild = (childSlug: string) => {
    navigateToChapter(childSlug);
  };

  return (
    <MobileTableOfContentsContainer $bottom={bottom}>
      <MobileTableOfContentsHandle onClick={() => setOpen(!open)}>
        {i18n('Table of Contents')}
      </MobileTableOfContentsHandle>
      <MobileTableOfContentsContent id="MobileTableOfContentsContent">
        {parents.map((parent, i) => (
          <Fragment key={[i, parent.url].join()}>
            <MobileTableOfContentsParentRow onClick={() => clickParent(parent)}>
              <MobileTableOfContentsParentRowIcon>
                {parent.children.length > 0 && (
                  <Button
                    type="button"
                    icon={{
                      name:
                        parent.url === selectedParent
                          ? 'chevron-up'
                          : 'chevron-down',
                      size: 14,
                    }}
                    variant="tertiary-grey"
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      if (parent.url === selectedParent) {
                        e.stopPropagation();
                        setSelectedParent(null);
                      }
                    }}
                  />
                )}
              </MobileTableOfContentsParentRowIcon>
              <div>{parent.heading}</div>
            </MobileTableOfContentsParentRow>
            <MobileTableOfContentsChildren
              $open={parent.url === selectedParent}
            >
              {parent.children.map((child) => (
                <MobileTableOfContentsChildRow
                  key={child.url}
                  onClick={() => {
                    clickChild(child.url);
                  }}
                >
                  {child.heading}
                </MobileTableOfContentsChildRow>
              ))}
            </MobileTableOfContentsChildren>
          </Fragment>
        ))}
      </MobileTableOfContentsContent>
    </MobileTableOfContentsContainer>
  );
}
